import React, { useState, useEffect, useCallback } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import { format } from 'date-fns';
import {
  Eye, EyeOff, Trash2, ChevronDown, ChevronUp, Star, X, AlertTriangle, ChevronLeft, ChevronRight
} from 'lucide-react';

const PAGE_SIZE = 25;
const STORAGE_BASE = 'https://vltfzozjztfaeifjqpcg.supabase.co/storage/v1/object/public/review-photos';

const RATING_OPTIONS = [
  { value: '', label: 'Tutte le stelle' },
  { value: '5', label: '★★★★★ 5' },
  { value: '4', label: '★★★★☆ 4' },
  { value: '3', label: '★★★☆☆ 3' },
  { value: '2', label: '★★☆☆☆ 2' },
  { value: '1', label: '★☆☆☆☆ 1' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Tutte' },
  { value: 'published', label: 'Pubblicate' },
  { value: 'hidden', label: 'Nascoste' },
];

const RESPONSE_OPTIONS = [
  { value: '', label: 'Tutte' },
  { value: 'with', label: 'Con risposta' },
  { value: 'without', label: 'Senza risposta' },
];

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-80"><X size={16} /></button>
  </div>
);

// ─── Photo Delete Confirmation Modal (with full-size preview) ─────────────────
const PhotoDeleteModal = ({ photo, photoUrl, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle size={22} className="text-red-500 flex-shrink-0" />
        <div>
          <p className="text-gray-900 font-semibold text-base">Eliminare questa foto?</p>
          <p className="text-gray-500 text-sm mt-0.5">Questa azione non può essere annullata</p>
        </div>
      </div>
      {photoUrl && (
        <div className="mb-5 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center" style={{ maxHeight: 280 }}>
          <img
            src={photoUrl}
            alt="Anteprima foto da eliminare"
            className="max-w-full max-h-72 object-contain"
          />
        </div>
      )}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Annulla
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700"
        >
          Elimina foto
        </button>
      </div>
    </div>
  </div>
);

// ─── Generic Confirm Modal ────────────────────────────────────────────────────
const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle size={22} className="text-yellow-500 flex-shrink-0" />
        <p className="text-gray-800 text-sm font-medium">{message}</p>
      </div>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Annulla
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700"
        >
          Conferma
        </button>
      </div>
    </div>
  </div>
);

// ─── Full-size Photo Modal ────────────────────────────────────────────────────
const PhotoModal = ({ photos, index, onClose, onPrev, onNext }) => {
  const url = photos?.[index];
  const hasPrev = index > 0;
  const hasNext = index < (photos?.length || 0) - 1;

  React.useEffect(() => {
    const handler = (e) => {
      if (e?.key === 'Escape') onClose();
      if (e?.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e?.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hasPrev, hasNext, onPrev, onNext, onClose]);

  if (!url) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
      <div className="relative max-w-4xl max-h-screen p-4" onClick={(e) => e?.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5 z-10"
          aria-label="Chiudi"
        >
          <X size={20} />
        </button>

        {hasPrev && (
          <button
            onClick={onPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 z-10"
            aria-label="Foto precedente"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        {hasNext && (
          <button
            onClick={onNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 z-10"
            aria-label="Foto successiva"
          >
            <ChevronRight size={28} />
          </button>
        )}

        <img src={url} alt="Foto recensione a schermo intero" className="max-w-full max-h-[85vh] object-contain rounded-lg" />

        {photos?.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
            {index + 1} / {photos?.length}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Star Rating ──────────────────────────────────────────────────────────────
const StarRating = ({ value }) => {
  if (!value) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5]?.map((s) => (
        <Star
          key={s}
          size={14}
          className={s <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
        />
      ))}
      <span className="ml-1 text-xs text-gray-600">{value}</span>
    </div>
  );
};

// ─── User Reviews Drawer ──────────────────────────────────────────────────────
const UserReviewsDrawer = ({ email, mode, onClose }) => {
  // mode: 'client' | 'professional'
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const title = mode === 'client'
    ? `Recensioni di ${email}`
    : `Recensioni ricevute da ${email}`;

  useEffect(() => {
    const fetchUserReviews = async () => {
      setLoading(true);
      try {
        // First find the user id by email
        const { data: userData } = await supabase
          ?.from('users')
          ?.select('id')
          ?.eq('email', email)
          ?.single();

        if (!userData?.id) {
          setReviews([]);
          setLoading(false);
          return;
        }

        let query = supabase
          ?.from('reviews')
          ?.select(`
            id, created_at, rating, comment,
            client:users!reviews_client_id_fkey(email),
            professional:users!reviews_professional_id_fkey(email)
          `)
          ?.order('created_at', { ascending: false });

        if (mode === 'client') {
          query = query?.eq('client_id', userData?.id);
        } else {
          query = query?.eq('professional_id', userData?.id);
        }

        const { data } = await query;
        setReviews(data || []);
      } catch (e) {
        console.warn('Drawer fetch failed:', e);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    if (email) fetchUserReviews();
  }, [email, mode]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return format(new Date(ts), 'dd/MM/yyyy'); } catch { return ts; }
  };

  const truncate = (text, len = 60) => {
    if (!text) return '—';
    return text?.length > len ? text?.slice(0, len) + '…' : text;
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-base font-bold text-gray-900 leading-tight">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200 text-gray-500">
            <X size={20} />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : reviews?.length === 0 ? (
            <p className="text-center text-gray-500 py-12 text-sm">Nessuna recensione trovata</p>
          ) : (
            <div className="space-y-3">
              {reviews?.map((r) => {
                const counterpart = mode === 'client'
                  ? r?.professional?.email
                  : r?.client?.email;
                return (
                  <div key={r?.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">{formatDate(r?.created_at)}</span>
                      <StarRating value={r?.rating} />
                    </div>
                    <p className="text-xs text-gray-500 mb-1">
                      {mode === 'client' ? 'Professionista:' : 'Cliente:'}{' '}
                      <span className="text-gray-700 font-medium">{counterpart || '—'}</span>
                    </p>
                    <p className="text-sm text-gray-700">{truncate(r?.comment, 60)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500">{reviews?.length} recension{reviews?.length === 1 ? 'e' : 'i'} trovate</p>
        </div>
      </div>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // KPI: Totale | Pubblicate | Nascoste | Senza risposta
  const [kpi, setKpi] = useState({ total: 0, published: 0, hidden: 0, noResponse: 0 });

  // Filters
  const [searchEmail, setSearchEmail] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [responseFilter, setResponseFilter] = useState('');

  // UI state
  const [expandedRow, setExpandedRow] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [photoDeleteModal, setPhotoDeleteModal] = useState(null); // { photo, reviewId, url }
  const [fullPhotoModal, setFullPhotoModal] = useState(null); // { photos: [urls], index: number }
  const [actionLoading, setActionLoading] = useState({});

  // Drawer state
  const [drawer, setDrawer] = useState(null); // { email, mode: 'client'|'professional' }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const getSession = async () => {
    const { data: { user }, error } = await supabase?.auth?.getUser();
    if (error || !user) throw new Error('Sessione non valida');
    return user;
  };

  const logAction = async (action, entityId, entityType, oldValue, newValue) => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      await supabase?.functions?.invoke('logAdminAction', {
        body: {
          action,
          entity_type: entityType || 'review',
          entity_id: String(entityId),
          old_value: oldValue,
          new_value: newValue,
        },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session?.access_token}` }
          : {},
      });
    } catch (e) {
      console.warn('Audit log failed (non-blocking):', e);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return '';
    if (photo?.storage_path) {
      return `${STORAGE_BASE}/${photo?.storage_path}`;
    }
    // Fallback to any existing url fields
    return photo?.url || photo?.photo_url || '';
  };

  const fetchKPIs = useCallback(async () => {
    try {
      const { data, error } = await supabase
        ?.from('reviews')
        ?.select('id, is_published, professional_response');
      if (error) return;
      const total = data?.length || 0;
      const published = data?.filter(r => r?.is_published)?.length || 0;
      const hidden = data?.filter(r => !r?.is_published)?.length || 0;
      const noResponse = data?.filter(r => !r?.professional_response)?.length || 0;
      setKpi({ total, published, hidden, noResponse });
    } catch (e) {
      console.warn('KPI fetch failed:', e);
    }
  }, []);

  const fetchReviews = useCallback(async (page, search, rating, status, response) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        ?.from('reviews')
        ?.select(`
          *,
          client:users!reviews_client_id_fkey(email),
          professional:users!reviews_professional_id_fkey(email),
          photos:review_photos(*)
        `, { count: 'exact' })
        ?.order('created_at', { ascending: false })
        ?.range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (search) {
        // Search by professional OR client email using OR filter
        query = query?.or(
          `professional.email.ilike.%${search}%,client.email.ilike.%${search}%`
        );
      }
      if (rating) {
        query = query?.eq('rating', parseInt(rating));
      }
      if (status === 'published') {
        query = query?.eq('is_published', true);
      } else if (status === 'hidden') {
        query = query?.eq('is_published', false);
      }
      if (response === 'with') {
        query = query?.not('professional_response', 'is', null);
      } else if (response === 'without') {
        query = query?.is('professional_response', null);
      }

      const { data, count, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      setReviews(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento delle recensioni');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews(currentPage, searchEmail, ratingFilter, statusFilter, responseFilter);
  }, [currentPage, searchEmail, ratingFilter, statusFilter, responseFilter, fetchReviews]);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  const refreshAll = () => {
    fetchReviews(currentPage, searchEmail, ratingFilter, statusFilter, responseFilter);
    fetchKPIs();
  };

  const handleSearchChange = (e) => {
    setSearchEmail(e?.target?.value);
    setCurrentPage(1);
  };

  // ─── Toggle Published ───────────────────────────────────────────────────────
  const handleTogglePublished = async (review) => {
    const newVal = !review?.is_published;
    setActionLoading(prev => ({ ...prev, [`pub_${review?.id}`]: true }));
    try {
      await getSession();
      const { error } = await supabase
        ?.from('reviews')
        ?.update({ is_published: newVal })
        ?.eq('id', review?.id);
      if (error) throw error;
      await logAction('moderate_review', review?.id, 'review',
        { is_published: review?.is_published },
        { is_published: newVal }
      );
      showToast(newVal ? 'Recensione pubblicata' : 'Recensione nascosta');
      refreshAll();
    } catch (e) {
      showToast(e?.message || 'Errore', 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [`pub_${review?.id}`]: false }));
    }
  };

  // ─── Remove Response ────────────────────────────────────────────────────────
  const handleRemoveResponse = (review) => {
    setConfirmModal({
      message: 'Rimuovere la risposta del professionista?',
      onConfirm: async () => {
        setConfirmModal(null);
        setActionLoading(prev => ({ ...prev, [`resp_${review?.id}`]: true }));
        try {
          await getSession();
          const { error } = await supabase
            ?.from('reviews')
            ?.update({ professional_response: null, professional_response_at: null })
            ?.eq('id', review?.id);
          if (error) throw error;
          await logAction('remove_review_response', review?.id, 'review',
            { professional_response: review?.professional_response },
            { professional_response: null }
          );
          showToast('Risposta rimossa');
          refreshAll();
        } catch (e) {
          showToast(e?.message || 'Errore', 'error');
        } finally {
          setActionLoading(prev => ({ ...prev, [`resp_${review?.id}`]: false }));
        }
      },
      onCancel: () => setConfirmModal(null),
    });
  };

  // ─── Delete Photo ───────────────────────────────────────────────────────────
  const handleDeletePhotoClick = (photo, reviewId) => {
    const url = getPhotoUrl(photo);
    setPhotoDeleteModal({ photo, reviewId, url });
  };

  const handleDeletePhotoConfirm = async () => {
    if (!photoDeleteModal) return;
    const { photo, reviewId } = photoDeleteModal;
    setPhotoDeleteModal(null);
    setActionLoading(prev => ({ ...prev, [`photo_${photo?.id}`]: true }));
    try {
      await getSession();
      // a) Delete from DB first
      const { error: dbError } = await supabase
        ?.from('review_photos')
        ?.delete()
        ?.eq('id', photo?.id);
      if (dbError) throw dbError;
      // b) Remove from storage
      if (photo?.storage_path) {
        const { error: storageError } = await supabase
          ?.storage
          ?.from('review-photos')
          ?.remove([photo?.storage_path]);
        if (storageError) console.warn('Storage delete failed (non-blocking):', storageError);
      }
      // c) Audit log
      await logAction('delete_review_photo', photo?.id, 'review_photo',
        { storage_path: photo?.storage_path },
        null
      );
      showToast('Foto eliminata');
      refreshAll();
    } catch (e) {
      showToast(e?.message || 'Errore eliminazione foto', 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [`photo_${photo?.id}`]: false }));
    }
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return format(new Date(ts), 'dd/MM/yyyy HH:mm'); } catch { return ts; }
  };

  const truncate = (text, len = 60) => {
    if (!text) return '—';
    return text?.length > len ? text?.slice(0, len) + '…' : text;
  };

  // ─── KPI Cards config ───────────────────────────────────────────────────────
  const kpiCards = [
    { label: 'Totale recensioni', value: kpi?.total, color: 'bg-blue-50 border-blue-200', textColor: 'text-blue-700' },
    { label: 'Pubblicate', value: kpi?.published, color: 'bg-green-50 border-green-200', textColor: 'text-green-700' },
    { label: 'Nascoste', value: kpi?.hidden, color: 'bg-gray-50 border-gray-300', textColor: 'text-gray-700' },
    { label: 'Senza risposta', value: kpi?.noResponse, color: 'bg-orange-50 border-orange-200', textColor: 'text-orange-700' },
  ];

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Recensioni</h1>
          <p className="text-sm text-gray-500 mt-1">Modera le recensioni degli utenti</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpiCards?.map((card) => (
            <div key={card?.label} className={`border rounded-lg p-4 ${card?.color}`}>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{card?.label}</p>
              <p className={`text-3xl font-bold mt-1 ${card?.textColor}`}>{card?.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Cerca per email professionista o cliente…"
            value={searchEmail}
            onChange={handleSearchChange}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[260px]"
          />
          <select
            value={ratingFilter}
            onChange={(e) => { setRatingFilter(e?.target?.value); setCurrentPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {RATING_OPTIONS?.map(opt => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e?.target?.value); setCurrentPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {STATUS_OPTIONS?.map(opt => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>
          <select
            value={responseFilter}
            onChange={(e) => { setResponseFilter(e?.target?.value); setCurrentPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {RESPONSE_OPTIONS?.map(opt => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500 ml-auto">{totalCount} risultati</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Professionista</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Testo</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Foto</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Risposta</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex items-center justify-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span>Caricamento…</span>
                      </div>
                    </td>
                  </tr>
                ) : reviews?.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      Nessuna recensione trovata
                    </td>
                  </tr>
                ) : (
                  reviews?.map((review) => {
                    const isExpanded = expandedRow === review?.id;
                    const photos = review?.photos || [];

                    // Clear state-based badge
                    const isPublished = review?.is_published;
                    const statusBadgeClass = isPublished
                      ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-600';
                    const statusLabel = isPublished ? 'Pubblica' : 'Nascosta';

                    return (
                      <React.Fragment key={review?.id}>
                        <tr
                          className={`hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
                          onClick={() => setExpandedRow(isExpanded ? null : review?.id)}
                        >
                          <td className="px-4 py-3 text-center text-gray-400">
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {formatDate(review?.created_at)}
                          </td>
                          {/* Cliente — clickable */}
                          <td className="px-4 py-3 text-sm max-w-[140px]" onClick={(e) => e?.stopPropagation()}>
                            {review?.client?.email ? (
                              <button
                                onClick={() => setDrawer({ email: review?.client?.email, mode: 'client' })}
                                className="text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-full text-left"
                                title={`Vedi tutte le recensioni di ${review?.client?.email}`}
                              >
                                {review?.client?.email}
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          {/* Professionista — clickable */}
                          <td className="px-4 py-3 text-sm max-w-[140px]" onClick={(e) => e?.stopPropagation()}>
                            {review?.professional?.email ? (
                              <button
                                onClick={() => setDrawer({ email: review?.professional?.email, mode: 'professional' })}
                                className="text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-full text-left"
                                title={`Vedi tutte le recensioni ricevute da ${review?.professional?.email}`}
                              >
                                {review?.professional?.email}
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <StarRating value={review?.rating} />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px]">
                            <span title={review?.comment || ''}>
                              {truncate(review?.comment, 60)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700">
                            {photos?.length > 0 ? (
                              <span className="inline-flex items-center justify-center bg-blue-100 text-blue-700 text-xs font-semibold rounded-full h-6 w-6">
                                {photos?.length}
                              </span>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {review?.professional_response ? (
                              <span className="inline-block bg-purple-100 text-purple-700 text-xs font-medium px-2 py-0.5 rounded-full">Sì</span>
                            ) : (
                              <span className="text-gray-400 text-xs">No</span>
                            )}
                          </td>
                          {/* Stato badge — green for published, gray for hidden */}
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${statusBadgeClass}`}>
                              {statusLabel}
                            </span>
                          </td>
                          {/* Azioni */}
                          <td className="px-4 py-3 text-center" onClick={(e) => e?.stopPropagation()}>
                            <div className="flex items-center justify-center gap-2">
                              {/* Toggle published: green Eye when published, gray EyeOff when hidden */}
                              {isPublished ? (
                                <button
                                  onClick={() => handleTogglePublished(review)}
                                  disabled={!!actionLoading?.[`pub_${review?.id}`]}
                                  title="Nascondi recensione"
                                  className="p-1.5 rounded hover:bg-green-50 text-green-600 hover:text-green-700 disabled:opacity-40"
                                >
                                  <Eye size={16} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleTogglePublished(review)}
                                  disabled={!!actionLoading?.[`pub_${review?.id}`]}
                                  title="Pubblica recensione"
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-40"
                                >
                                  <EyeOff size={16} />
                                </button>
                              )}
                              {/* Remove response */}
                              {review?.professional_response && (
                                <button
                                  onClick={() => handleRemoveResponse(review)}
                                  disabled={!!actionLoading?.[`resp_${review?.id}`]}
                                  title="Rimuovi risposta professionista"
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500 disabled:opacity-40"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Expanded row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="10" className="bg-blue-50 px-6 py-5 border-b border-blue-100">
                              <div className="space-y-4">
                                {/* Full review text */}
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Testo completo</p>
                                  <blockquote className="text-sm text-gray-800 whitespace-pre-wrap border-l-4 border-blue-300 pl-4 italic bg-white rounded-r-lg py-2 pr-3">
                                    {review?.comment || '—'}
                                  </blockquote>
                                </div>

                                {/* Detailed ratings */}
                                {(review?.punctuality_rating != null || review?.quality_rating != null || review?.price_rating != null) && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Valutazioni dettagliate</p>
                                    <div className="flex flex-wrap gap-4">
                                      {review?.punctuality_rating != null && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500">Puntualità:</span>
                                          <StarRating value={review?.punctuality_rating} />
                                        </div>
                                      )}
                                      {review?.quality_rating != null && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500">Qualità:</span>
                                          <StarRating value={review?.quality_rating} />
                                        </div>
                                      )}
                                      {review?.price_rating != null && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500">Prezzo:</span>
                                          <StarRating value={review?.price_rating} />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Professional response */}
                                {review?.professional_response && (
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Risposta del professionista</p>
                                      {review?.professional_response_at && (
                                        <span className="text-xs text-gray-400">{formatDate(review?.professional_response_at)}</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap bg-white border border-gray-200 rounded-lg p-3">
                                      {review?.professional_response}
                                    </p>
                                  </div>
                                )}

                                {/* Photo gallery */}
                                {photos?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Foto ({photos?.length})</p>
                                    <div className="flex flex-wrap gap-3">
                                      {photos?.map((photo) => {
                                        const photoUrl = getPhotoUrl(photo);
                                        return (
                                          <div
                                            key={photo?.id}
                                            className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-100 cursor-pointer"
                                            style={{ width: 120, height: 120, flexShrink: 0 }}
                                            onClick={() => {
                                              const urls = (photos || [])?.map(p => getPhotoUrl(p))?.filter(Boolean);
                                              const idx = urls?.indexOf(photoUrl);
                                              if (idx >= 0) setFullPhotoModal({ photos: urls, index: idx });
                                            }}
                                          >
                                            {photoUrl ? (
                                              <img
                                                src={photoUrl}
                                                alt="Foto recensione"
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                  e?.target?.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                                }}
                                              />
                                            ) : null}
                                            <div className="hidden w-full h-full items-center justify-center text-gray-400 text-xs">
                                              Foto non disponibile
                                            </div>
                                            {/* Hover overlay with trash at top-right */}
                                            <div
                                              className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all pointer-events-none"
                                            >
                                              <button
                                                onClick={(e) => {
                                                  e?.stopPropagation();
                                                  handleDeletePhotoClick(photo, review?.id);
                                                }}
                                                disabled={!!actionLoading?.[`photo_${photo?.id}`]}
                                                className="pointer-events-auto absolute top-1.5 right-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 z-10"
                                                title="Elimina foto"
                                              >
                                                <Trash2 size={12} />
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <p className="text-sm text-gray-700">
                Risultati{' '}
                <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>–
                <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, totalCount)}</span>{' '}
                di <span className="font-medium">{totalCount}</span>
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Precedente
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Successiva
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
      {/* Toast */}
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
      {/* Generic Confirm Modal (for response removal) */}
      {confirmModal && (
        <ConfirmModal
          message={confirmModal?.message}
          onConfirm={confirmModal?.onConfirm}
          onCancel={confirmModal?.onCancel}
        />
      )}
      {/* Photo Delete Confirmation Modal with full-size preview */}
      {photoDeleteModal && (
        <PhotoDeleteModal
          photo={photoDeleteModal?.photo}
          photoUrl={photoDeleteModal?.url}
          onConfirm={handleDeletePhotoConfirm}
          onCancel={() => setPhotoDeleteModal(null)}
        />
      )}
      {/* Full-size Photo Viewer Modal */}
      {fullPhotoModal && (
        <PhotoModal
          photos={fullPhotoModal?.photos}
          index={fullPhotoModal?.index}
          onClose={() => setFullPhotoModal(null)}
          onPrev={() => setFullPhotoModal({ ...fullPhotoModal, index: Math.max(0, fullPhotoModal?.index - 1) })}
          onNext={() => setFullPhotoModal({ ...fullPhotoModal, index: Math.min(fullPhotoModal?.photos?.length - 1, fullPhotoModal?.index + 1) })}
        />
      )}
      {/* User Reviews Drawer */}
      {drawer && (
        <UserReviewsDrawer
          email={drawer?.email}
          mode={drawer?.mode}
          onClose={() => setDrawer(null)}
        />
      )}
    </AdminDashboardLayout>
  );
};

export default AdminReviewsManagement;
